import { API_BASE } from '@/shared/services/api';
import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Badge } from '@/shared/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/shared/components/ui/dialog';
import { Label } from '@/shared/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/components/ui/select';
import { Textarea } from '@/shared/components/ui/textarea';
import { toast } from 'sonner';
import {
  FileText, Plus, RefreshCw, Eye, CheckCircle, Send, RotateCcw,
  MoreHorizontal, AlertCircle, DollarSign } from
'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from
'@/shared/components/ui/dropdown-menu';

const API_URL = API_BASE;
const getAuthHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${localStorage.getItem('ezerpToken')}`
});

const statusColors = {
  draft: 'bg-gray-500/20 text-gray-400',
  pending_approval: 'bg-amber-500/20 text-amber-400',
  approved: 'bg-blue-500/20 text-blue-400',
  posted: 'bg-green-500/20 text-green-400',
  reversed: 'bg-red-500/20 text-red-400'
};

export const JournalEntries = () => {const { t } = useTranslation("finance");
  const [journals, setJournals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [showCreate, setShowCreate] = useState(false);
  const [showDetails, setShowDetails] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [formData, setFormData] = useState({
    period_code: '', posting_date: '', memo: '', entry_type: 'standard',
    lines: [
    { account_id: '', debit: 0, credit: 0, memo: '' },
    { account_id: '', debit: 0, credit: 0, memo: '' }]

  });

  const fetchJournals = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/accounting/journals?`;
      if (statusFilter !== 'all') url += `status=${statusFilter}&`;
      const res = await fetch(url, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Failed to fetch journals');
      const data = await res.json();
      setJournals(data.journals || []);
    } catch (err) {
      toast.error(err.message);
      setJournals([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchAccounts = async () => {
    try {
      const res = await fetch(`${API_URL}/api/accounting/chart-of-accounts`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setAccounts((data.accounts || []).filter((a) => !a.is_header));
      }
    } catch (err) {console.error(err);}
  };

  useEffect(() => {fetchJournals();fetchAccounts();}, [statusFilter]);

  const handleCreate = async () => {
    try {
      // Validate totals match
      const totalDebit = formData.lines.reduce((sum, l) => sum + (parseFloat(l.debit) || 0), 0);
      const totalCredit = formData.lines.reduce((sum, l) => sum + (parseFloat(l.credit) || 0), 0);
      if (Math.abs(totalDebit - totalCredit) > 0.01) {
        toast.error('Debits must equal credits');
        return;
      }

      const payload = {
        ...formData,
        lines: formData.lines.filter((l) => l.account_id).map((l) => ({
          ...l,
          account_id: parseInt(l.account_id),
          debit: parseFloat(l.debit) || 0,
          credit: parseFloat(l.credit) || 0
        }))
      };

      const res = await fetch(`${API_URL}/api/accounting/journals/manual`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload)
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to create journal');
      }
      toast.success('Journal entry created');
      setShowCreate(false);
      setFormData({
        period_code: '', posting_date: '', memo: '', entry_type: 'standard',
        lines: [
        { account_id: '', debit: 0, credit: 0, memo: '' },
        { account_id: '', debit: 0, credit: 0, memo: '' }]

      });
      fetchJournals();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handlePost = async (id) => {
    try {
      const res = await fetch(`${API_URL}/api/accounting/journals/${id}/post`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      if (!res.ok) throw new Error('Failed to post journal');
      toast.success('Journal posted');
      fetchJournals();
    } catch (err) {toast.error(err.message);}
  };

  const handleReverse = async (id) => {
    const reason = window.prompt('Enter reversal reason:');
    if (!reason) return;
    try {
      const res = await fetch(`${API_URL}/api/accounting/journals/${id}/reverse?reason=${encodeURIComponent(reason)}`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      if (!res.ok) throw new Error('Failed to reverse journal');
      toast.success('Journal reversed');
      fetchJournals();
    } catch (err) {toast.error(err.message);}
  };

  const viewDetails = async (id) => {
    try {
      const res = await fetch(`${API_URL}/api/accounting/journals/${id}`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Failed to fetch journal details');
      const data = await res.json();
      setShowDetails(data);
    } catch (err) {toast.error(err.message);}
  };

  const addLine = () => {
    setFormData((prev) => ({
      ...prev,
      lines: [...prev.lines, { account_id: '', debit: 0, credit: 0, memo: '' }]
    }));
  };

  const updateLine = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      lines: prev.lines.map((line, i) => i === index ? { ...line, [field]: value } : line)
    }));
  };

  const totalDebit = formData.lines.reduce((sum, l) => sum + (parseFloat(l.debit) || 0), 0);
  const totalCredit = formData.lines.reduce((sum, l) => sum + (parseFloat(l.credit) || 0), 0);
  const isBalanced = Math.abs(totalDebit - totalCredit) < 0.01;

  return (
    <div className="space-y-6" data-testid="journal-entries-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Space_Grotesk']">{t("journalEntries", "Journal Entries")}</h1>
          <p className="text-muted-foreground mt-1">{t("createAndManage", "Create and manage accounting journal entries")}</p>
        </div>
        <Button onClick={() => setShowCreate(true)} className="gap-2 bg-green-600 hover:bg-green-700">
          <Plus className="h-4 w-4" />{t("newJournalEntry", "New Journal Entry")}
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex gap-4">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("allStatus", "All Status")}</SelectItem>
                <SelectItem value="draft">{t("draft", "Draft")}</SelectItem>
                <SelectItem value="pending_approval">{t("pendingApproval", "Pending Approval")}</SelectItem>
                <SelectItem value="approved">{t("approved", "Approved")}</SelectItem>
                <SelectItem value="posted">{t("posted", "Posted")}</SelectItem>
                <SelectItem value="reversed">{t("reversed", "Reversed")}</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={fetchJournals}><RefreshCw className="h-4 w-4" /></Button>
          </div>
        </CardContent>
      </Card>

      {/* Journals Table */}
      <Card>
        <CardContent className="p-0">
          {loading ?
          <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500" />
            </div> :
          journals.length === 0 ?
          <div className="text-center py-16">
              <FileText className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
              <p className="text-lg text-muted-foreground">{t("noJournalEntries", "No journal entries found")}</p>
              <Button onClick={() => setShowCreate(true)} className="mt-4 bg-green-600 hover:bg-green-700">
                <Plus className="h-4 w-4 mr-2" />{t("createFirstEntry", "Create First Entry")}
            </Button>
            </div> :

          <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-800/50">
                  <tr>
                    <th className="text-left p-3 font-medium">{t("journal", "Journal #")}</th>
                    <th className="text-left p-3 font-medium">{t("period", "Period")}</th>
                    <th className="text-left p-3 font-medium">{t("date", "Date")}</th>
                    <th className="text-left p-3 font-medium">{t("type", "Type")}</th>
                    <th className="text-left p-3 font-medium">{t("debit", "Debit")}</th>
                    <th className="text-left p-3 font-medium">{t("credit", "Credit")}</th>
                    <th className="text-left p-3 font-medium">{t("status", "Status")}</th>
                    <th className="text-right p-3 font-medium">{t("actions", "Actions")}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700">
                  {journals.map((journal) =>
                <tr key={journal.id} className="hover:bg-slate-800/30">
                      <td className="p-3 font-medium">{journal.journal_number || `#${journal.id}`}</td>
                      <td className="p-3">{journal.period_code}</td>
                      <td className="p-3">{journal.posting_date}</td>
                      <td className="p-3 capitalize">{journal.entry_type}</td>
                      <td className="p-3 font-medium">${(journal.total_debit || 0).toLocaleString()}</td>
                      <td className="p-3 font-medium">${(journal.total_credit || 0).toLocaleString()}</td>
                      <td className="p-3">
                        <Badge className={statusColors[journal.status]}>{journal.status}</Badge>
                      </td>
                      <td className="p-3 text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon"><MoreHorizontal className="h-4 w-4" /></Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => viewDetails(journal.id)}>
                              <Eye className="h-4 w-4 mr-2" />{t("viewDetails", "View Details")}
                        </DropdownMenuItem>
                            {(journal.status === 'draft' || journal.status === 'approved') &&
                        <DropdownMenuItem onClick={() => handlePost(journal.id)}>
                                <Send className="h-4 w-4 mr-2 text-green-500" />{t("post", "Post")}
                        </DropdownMenuItem>
                        }
                            {journal.status === 'posted' &&
                        <DropdownMenuItem onClick={() => handleReverse(journal.id)} className="text-red-400">
                                <RotateCcw className="h-4 w-4 mr-2" />{t("reverse", "Reverse")}
                        </DropdownMenuItem>
                        }
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                )}
                </tbody>
              </table>
            </div>
          }
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{t("newJournalEntry", "New Journal Entry")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4 max-h-[70vh] overflow-y-auto">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>{t("periodCode", "Period Code *")}</Label>
                <Input value={formData.period_code} onChange={(e) => setFormData({ ...formData, period_code: e.target.value })} placeholder="2026-02" />
              </div>
              <div>
                <Label>{t("postingDate", "Posting Date *")}</Label>
                <Input type="date" value={formData.posting_date} onChange={(e) => setFormData({ ...formData, posting_date: e.target.value })} />
              </div>
              <div>
                <Label>{t("entryType", "Entry Type")}</Label>
                <Select value={formData.entry_type} onValueChange={(v) => setFormData({ ...formData, entry_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="standard">{t("standard", "Standard")}</SelectItem>
                    <SelectItem value="adjusting">{t("adjusting", "Adjusting")}</SelectItem>
                    <SelectItem value="closing">{t("closing", "Closing")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>{t("memo", "Memo")}</Label>
              <Textarea value={formData.memo} onChange={(e) => setFormData({ ...formData, memo: e.target.value })} placeholder="Journal description" />
            </div>

            {/* Journal Lines */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>{t("journalLines", "Journal Lines")}</Label>
                <Button variant="outline" size="sm" onClick={addLine}><Plus className="h-4 w-4 mr-1" />{t("addLine", "Add Line")}</Button>
              </div>
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-800/50">
                    <tr>
                      <th className="text-left p-2">{t("account", "Account")}</th>
                      <th className="text-left p-2 w-28">{t("debit", "Debit")}</th>
                      <th className="text-left p-2 w-28">{t("credit", "Credit")}</th>
                      <th className="text-left p-2">{t("memo", "Memo")}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-700">
                    {formData.lines.map((line, idx) =>
                    <tr key={idx}>
                        <td className="p-2">
                          <Select value={line.account_id} onValueChange={(v) => updateLine(idx, 'account_id', v)}>
                            <SelectTrigger><SelectValue placeholder="Select account" /></SelectTrigger>
                            <SelectContent>
                              {accounts.map((a) =>
                            <SelectItem key={a.id} value={String(a.id)}>{a.code} - {a.name}</SelectItem>
                            )}
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="p-2">
                          <Input type="number" value={line.debit} onChange={(e) => updateLine(idx, 'debit', e.target.value)} />
                        </td>
                        <td className="p-2">
                          <Input type="number" value={line.credit} onChange={(e) => updateLine(idx, 'credit', e.target.value)} />
                        </td>
                        <td className="p-2">
                          <Input value={line.memo} onChange={(e) => updateLine(idx, 'memo', e.target.value)} placeholder="Line memo" />
                        </td>
                      </tr>
                    )}
                  </tbody>
                  <tfoot className="bg-slate-800/30">
                    <tr>
                      <td className="p-2 font-medium">{t("total", "Total")}</td>
                      <td className="p-2 font-medium">${totalDebit.toLocaleString()}</td>
                      <td className="p-2 font-medium">${totalCredit.toLocaleString()}</td>
                      <td className="p-2">
                        {isBalanced ?
                        <span className="text-green-400 flex items-center gap-1"><CheckCircle className="h-4 w-4" />{t("balanced", "Balanced")}</span> :

                        <span className="text-red-400 flex items-center gap-1"><AlertCircle className="h-4 w-4" />{t("unbalanced", "Unbalanced")}</span>
                        }
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>{t("cancel", "Cancel")}</Button>
            <Button onClick={handleCreate} className="bg-green-600 hover:bg-green-700" disabled={!isBalanced}>{t("createJournal", "Create Journal")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Details Dialog */}
      <Dialog open={!!showDetails} onOpenChange={() => setShowDetails(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t("journalEntryDetails", "Journal Entry Details")}</DialogTitle>
          </DialogHeader>
          {showDetails &&
          <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="p-3 rounded-lg bg-slate-800/50">
                  <p className="text-sm text-muted-foreground">{t("journal", "Journal #")}</p>
                  <p className="font-bold">{showDetails.journal_number || `#${showDetails.id}`}</p>
                </div>
                <div className="p-3 rounded-lg bg-slate-800/50">
                  <p className="text-sm text-muted-foreground">{t("period", "Period")}</p>
                  <p className="font-bold">{showDetails.period_code}</p>
                </div>
                <div className="p-3 rounded-lg bg-slate-800/50">
                  <p className="text-sm text-muted-foreground">{t("status", "Status")}</p>
                  <Badge className={statusColors[showDetails.status]}>{showDetails.status}</Badge>
                </div>
              </div>
              {showDetails.memo &&
            <div className="p-3 rounded-lg bg-slate-800/50">
                  <p className="text-sm text-muted-foreground">{t("memo", "Memo")}</p>
                  <p>{showDetails.memo}</p>
                </div>
            }
              {showDetails.lines && showDetails.lines.length > 0 &&
            <div>
                  <h4 className="font-semibold mb-2">{t("lines", "Lines")}</h4>
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-800/50">
                        <tr>
                          <th className="text-left p-2">{t("account", "Account")}</th>
                          <th className="text-right p-2">{t("debit", "Debit")}</th>
                          <th className="text-right p-2">{t("credit", "Credit")}</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-700">
                        {showDetails.lines.map((line, idx) =>
                    <tr key={idx}>
                            <td className="p-2">{line.account_code} - {line.account_name}</td>
                            <td className="p-2 text-right">{line.debit > 0 ? `$${line.debit.toLocaleString()}` : '-'}</td>
                            <td className="p-2 text-right">{line.credit > 0 ? `$${line.credit.toLocaleString()}` : '-'}</td>
                          </tr>
                    )}
                      </tbody>
                      <tfoot className="bg-slate-800/30">
                        <tr>
                          <td className="p-2 font-bold">{t("total", "Total")}</td>
                          <td className="p-2 text-right font-bold">${(showDetails.total_debit || 0).toLocaleString()}</td>
                          <td className="p-2 text-right font-bold">${(showDetails.total_credit || 0).toLocaleString()}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
            }
            </div>
          }
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetails(null)}>{t("close", "Close")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>);

};

export default JournalEntries;